const RICH_TEXT_EDITOR_ID = 'rich-text-editor';

const escapeHTML = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const textStyleAt = (object, line, character) => {
  const style =
    object.styles && object.styles[line] && object.styles[line][character]
      ? object.styles[line][character]
      : {};

  return {
    bold: (style.fontWeight || object.fontWeight) === 'bold',
    italic: (style.fontStyle || object.fontStyle) === 'italic',
  };
};

const editorHTML = object => {
  const text = object && object.text ? object.text : '';
  return text
    .split('\n')
    .map((line, lineIndex) => {
      const characters = [...line]
        .map((character, characterIndex) => {
          const { bold, italic } = textStyleAt(
            object,
            lineIndex,
            characterIndex
          );
          let output = escapeHTML(character);
          if (italic) output = `<em>${output}</em>`;
          if (bold) output = `<strong>${output}</strong>`;
          return output;
        })
        .join('');
      return characters || '<br>';
    })
    .join('<br>');
};

const appendNewline = value => {
  if (value.text && !value.text.endsWith('\n')) {
    value.text += '\n';
    value.line += 1;
    value.character = 0;
  }
};

const addCharacter = (value, character, state) => {
  if (character === '\n') {
    appendNewline(value);
    return;
  }
  value.text += character;
  if (!value.styles[value.line]) value.styles[value.line] = {};
  value.styles[value.line][value.character] = {
    fontWeight: state.bold ? 'bold' : 'normal',
    fontStyle: state.italic ? 'italic' : 'normal',
  };
  value.character += 1;
};

const parseNode = (node, value, state) => {
  if (node.nodeType === Node.TEXT_NODE) {
    [...node.nodeValue].forEach(character =>
      addCharacter(value, character, state)
    );
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const tag = node.tagName.toLowerCase();
  if (tag === 'br') {
    appendNewline(value);
    return;
  }
  if (tag === 'ul' || tag === 'ol') {
    [...node.children].forEach((item, index) => {
      if (item.tagName.toLowerCase() !== 'li') return;
      if (value.text && !value.text.endsWith('\n')) appendNewline(value);
      const marker = tag === 'ul' ? '• ' : `${index + 1}. `;
      [...marker].forEach(character => addCharacter(value, character, state));
      [...item.childNodes].forEach(child => parseNode(child, value, state));
      appendNewline(value);
    });
    return;
  }

  const nextState = {
    bold: state.bold || tag === 'strong' || tag === 'b',
    italic: state.italic || tag === 'em' || tag === 'i',
  };
  const isBlock = tag === 'div' || tag === 'p' || tag === 'li';
  if (isBlock && value.text && !value.text.endsWith('\n')) appendNewline(value);
  [...node.childNodes].forEach(child => parseNode(child, value, nextState));
  if (isBlock) appendNewline(value);
};

const getRichTextValue = editor => {
  const value = { text: '', styles: {}, line: 0, character: 0 };
  [...editor.childNodes].forEach(node =>
    parseNode(node, value, { bold: false, italic: false })
  );

  // Browsers commonly add one trailing block/newline to contenteditable fields.
  value.text = value.text.replace(/\n+$/, '');
  const lineCount = value.text ? value.text.split('\n').length : 0;
  Object.keys(value.styles).forEach(line => {
    if (Number(line) >= lineCount) delete value.styles[line];
  });

  return { text: value.text, styles: value.styles };
};

const richTextEditorMarkup = object => `
  <div class="rich-text-editor">
    <div class="rich-text-editor__toolbar" role="toolbar" aria-label="Text formatting">
      <button type="button" class="btn btn-outline-secondary rich-text-editor__action" data-rich-text-command="bold" aria-label="Bold" title="Bold"><strong>B</strong></button>
      <button type="button" class="btn btn-outline-secondary rich-text-editor__action" data-rich-text-command="italic" aria-label="Italic" title="Italic"><em>I</em></button>
      <button type="button" class="btn btn-outline-secondary rich-text-editor__action" data-rich-text-command="insertUnorderedList" aria-label="Bulleted list" title="Bulleted list"><i class="fa-solid fa-list-ul" aria-hidden="true"></i></button>
      <button type="button" class="btn btn-outline-secondary rich-text-editor__action" data-rich-text-command="insertOrderedList" aria-label="Numbered list" title="Numbered list"><i class="fa-solid fa-list-ol" aria-hidden="true"></i></button>
    </div>
    <div id="${RICH_TEXT_EDITOR_ID}" class="rich-text-editor__content form-control" contenteditable="true" role="textbox" aria-multiline="true" aria-label="Text editor">${editorHTML(
  object
)}</div>
    <p class="form-text">Formatting is limited to bold, italic, and lists.</p>
  </div>
`;

const initRichTextEditor = () => {
  const editor = document.getElementById(RICH_TEXT_EDITOR_ID);
  if (!editor) return;

  editor
    .closest('.rich-text-editor')
    .querySelectorAll('[data-rich-text-command]')
    .forEach(button => {
      button.addEventListener('mousedown', event => event.preventDefault());
      button.addEventListener('click', () => {
        editor.focus();
        document.execCommand(button.dataset.richTextCommand, false, null);
      });
    });
};

export { getRichTextValue, initRichTextEditor, richTextEditorMarkup };
